import React, { useState, useEffect, useCallback } from 'react';
import VoxSmythIcons from './VoxSmythIcons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://voxsmyth.com';

const ResetPasswordPage = ({ onNavigateHome }) => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [token, setToken] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  // Design tokens matching VoxSmyth theme
  const RESET_TOKENS = {
    colors: {
      slate: '#2C3A4C',
      literary_gold: '#C8860D',
      antique_gold: '#D7B377',
      warm_parchment: '#F9F6F0',
      rich_parchment: '#E8DCC0',
      error_red: '#E74C3C',
      success_green: '#27AE60'
    },
    typography: {
      main_title: 'DM Serif Display, Georgia, serif',
      label: 'DM Sans, sans-serif',
      input: 'Inter, sans-serif',
      button: 'DM Sans, sans-serif'
    },
    layout: {
      form_width: '480px',
      input_height: '48px'
    }
  };

  const validateToken = useCallback(async (tokenToValidate) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/validate-reset-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tokenToValidate })
      });

      if (response.ok) {
        setTokenValid(true);
      } else {
        setError('This reset link has expired or is invalid. Please request a new password reset.');
        setTokenValid(false);
      }
    } catch (error) {
      setError('Unable to validate reset link. Please try again.');
      setTokenValid(false);
    } finally {
      setValidatingToken(false);
    }
  }, []);

  // Extract token from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');

    if (tokenParam) {
      setToken(tokenParam);
      validateToken(tokenParam);
    } else {
      setError('Invalid reset link. Please request a new password reset.');
      setValidatingToken(false);
    }
  }, [validateToken]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(''); // Clear errors when user starts typing
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      return 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('🔒 Resetting password with token:', token.substring(0, 10) + '...');
      
      const response = await fetch(`${BACKEND_URL}/api/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token,
          new_password: formData.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        console.log('✅ Password reset successful');
        setIsSubmitted(true);
      } else {
        setError(extractErrorMessage(data, 'Failed to reset password'));
      }
    } catch (error) {
      console.error('❌ Password reset failed:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (isSubmitted) {
    return (
      <div 
        style={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${RESET_TOKENS.colors.slate} 0%, #1A202C 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: window.innerWidth < 768 ? '20px 16px' : '40px 24px'
        }}
      >
        <div className="relative z-10 text-center max-w-md mx-auto">
          <div style={{ marginBottom: '32px' }}>
            <VoxSmythIcons.CheckCircle size={64} color={RESET_TOKENS.colors.success_green} />
          </div>
          
          <h1
            style={{
              fontFamily: RESET_TOKENS.typography.main_title,
              fontSize: window.innerWidth < 768 ? '28px' : '36px',
              color: 'white',
              marginBottom: '16px'
            }}
          >
            Password Reset Complete
          </h1>
          
          <p
            style={{
              fontFamily: RESET_TOKENS.typography.input,
              fontSize: '18px',
              color: RESET_TOKENS.colors.rich_parchment,
              marginBottom: '32px',
              lineHeight: '1.6'
            }}
          >
            Your password has been successfully updated. You can now sign in with your new password.
          </p>
          
          <button
            onClick={() => window.location.href = '/auth'}
            style={{
              background: `linear-gradient(135deg, ${RESET_TOKENS.colors.antique_gold} 0%, #E6C68A 100%)`,
              color: RESET_TOKENS.colors.slate,
              border: 'none',
              padding: '16px 32px',
              borderRadius: '12px',
              fontSize: '16px',
              fontFamily: RESET_TOKENS.typography.button,
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = `0 8px 24px ${RESET_TOKENS.colors.antique_gold}40`;
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            Continue to Sign In
          </button>
        </div>
      </div>
    );
  }

  // Token validation loading state
  if (validatingToken) {
    return (
      <div 
        style={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${RESET_TOKENS.colors.slate} 0%, #1A202C 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: window.innerWidth < 768 ? '20px 16px' : '40px 24px'
        }}
      >
        <div className="relative z-10 text-center max-w-md mx-auto">
          <div style={{ marginBottom: '24px' }}>
            <VoxSmythIcons.Loading size={48} color={RESET_TOKENS.colors.antique_gold} />
          </div>
          
          <h2
            style={{
              fontFamily: RESET_TOKENS.typography.main_title,
              fontSize: '24px',
              color: 'white',
              marginBottom: '12px'
            }}
          >
            Validating Reset Link
          </h2>
          
          <p
            style={{
              fontFamily: RESET_TOKENS.typography.input,
              fontSize: '16px',
              color: RESET_TOKENS.colors.rich_parchment
            }}
          >
            Please wait while we verify your password reset link...
          </p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div 
        style={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${RESET_TOKENS.colors.slate} 0%, #1A202C 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: window.innerWidth < 768 ? '20px 16px' : '40px 24px'
        }}
      >
        <div className="relative z-10 text-center max-w-md mx-auto">
          <div style={{ marginBottom: '24px' }}>
            <VoxSmythIcons.AlertTriangle size={48} color={RESET_TOKENS.colors.error_red} />
          </div>
          
          <h2
            style={{
              fontFamily: RESET_TOKENS.typography.main_title,
              fontSize: '24px',
              color: 'white',
              marginBottom: '12px'
            }}
          >
            Invalid Reset Link
          </h2>
          
          <p
            style={{
              fontFamily: RESET_TOKENS.typography.input,
              fontSize: '16px',
              color: RESET_TOKENS.colors.rich_parchment,
              marginBottom: '24px'
            }}
          >
            {error}
          </p>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => window.location.href = '/forgot-password'}
              style={{
                background: `linear-gradient(135deg, ${RESET_TOKENS.colors.antique_gold} 0%, #E6C68A 100%)`,
                color: RESET_TOKENS.colors.slate,
                border: 'none',
                padding: '12px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: RESET_TOKENS.typography.button,
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              Request New Reset Link
            </button>
            
            <button
              onClick={onNavigateHome}
              style={{
                background: 'transparent',
                border: `1px solid ${RESET_TOKENS.colors.antique_gold}`,
                borderRadius: '8px',
                color: RESET_TOKENS.colors.antique_gold,
                padding: '12px 20px',
                fontSize: '14px',
                fontFamily: RESET_TOKENS.typography.button,
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main reset form
  return (
    <div 
      style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${RESET_TOKENS.colors.slate} 0%, #1A202C 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: window.innerWidth < 768 ? '20px 16px' : '40px 24px'
      }}
    >
      {/* Navigation */}
      <nav style={{ 
        position: 'absolute', 
        top: window.innerWidth < 768 ? '16px' : '24px', 
        left: window.innerWidth < 768 ? '16px' : '24px',
        zIndex: 10
      }}>
        <button
          onClick={onNavigateHome}
          style={{
            background: 'transparent',
            border: `1px solid ${RESET_TOKENS.colors.antique_gold}60`,
            borderRadius: '8px',
            color: 'white',
            padding: window.innerWidth < 768 ? '8px 16px' : '12px 20px',
            fontSize: window.innerWidth < 768 ? '12px' : '14px',
            fontFamily: RESET_TOKENS.typography.button,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = RESET_TOKENS.colors.antique_gold;
            e.target.style.background = `${RESET_TOKENS.colors.antique_gold}15`;
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = `${RESET_TOKENS.colors.antique_gold}60`;
            e.target.style.background = 'transparent';
          }}
        >
          <VoxSmythIcons.ArrowLeft size={14} color={RESET_TOKENS.colors.antique_gold} />
          Home
        </button>
      </nav>

      {/* Main Content */}
      <div className="relative z-10">
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '20px',
            padding: window.innerWidth < 768 ? '32px 24px' : '48px',
            width: '100%',
            maxWidth: window.innerWidth < 768 ? '100%' : RESET_TOKENS.layout.form_width,
            boxShadow: '0 20px 60px rgba(26, 32, 44, 0.3)',
            border: `1px solid ${RESET_TOKENS.colors.antique_gold}20`
          }}
        >
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: window.innerWidth < 768 ? '24px' : '32px' }}>
            <div style={{ marginBottom: '16px' }}>
              <VoxSmythIcons.Key size={window.innerWidth < 768 ? 40 : 48} color={RESET_TOKENS.colors.literary_gold} />
            </div>
            
            <h1
              style={{
                fontFamily: RESET_TOKENS.typography.main_title,
                fontSize: window.innerWidth < 768 ? '24px' : '32px',
                color: RESET_TOKENS.colors.slate,
                margin: '0 0 8px 0'
              }}
            >
              Create New Password
            </h1>
            
            <p
              style={{
                fontFamily: RESET_TOKENS.typography.input,
                fontSize: window.innerWidth < 768 ? '14px' : '16px',
                color: `${RESET_TOKENS.colors.slate}80`,
                margin: 0,
                maxWidth: '400px',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
            >
              Please enter a new password for your VoxSmyth account.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div
              style={{
                background: RESET_TOKENS.colors.error_red + '15',
                border: `1px solid ${RESET_TOKENS.colors.error_red}30`,
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '24px',
                color: RESET_TOKENS.colors.error_red,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <VoxSmythIcons.AlertTriangle size={16} color={RESET_TOKENS.colors.error_red} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* New Password Input */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontFamily: RESET_TOKENS.typography.label,
                  fontSize: '14px',
                  fontWeight: '500',
                  color: RESET_TOKENS.colors.slate,
                  marginBottom: '6px'
                }}
              >
                New Password
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                required
                placeholder="Enter your new password"
                style={{
                  width: '100%',
                  height: RESET_TOKENS.layout.input_height,
                  padding: '0 16px',
                  border: `1px solid ${RESET_TOKENS.colors.slate}30`,
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontFamily: RESET_TOKENS.typography.input,
                  color: RESET_TOKENS.colors.slate,
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = RESET_TOKENS.colors.literary_gold;
                  e.target.style.boxShadow = `0 0 0 3px ${RESET_TOKENS.colors.literary_gold}20`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = `${RESET_TOKENS.colors.slate}30`;
                  e.target.style.boxShadow = 'none';
                }}
              />
              <small style={{ 
                color: `${RESET_TOKENS.colors.slate}60`,
                fontSize: '12px'
              }}>
                Password must be at least 8 characters with uppercase, lowercase, and number
              </small>
            </div>

            {/* Confirm Password Input */}
            <div style={{ marginBottom: '32px' }}>
              <label
                style={{
                  display: 'block',
                  fontFamily: RESET_TOKENS.typography.label,
                  fontSize: '14px',
                  fontWeight: '500',
                  color: RESET_TOKENS.colors.slate,
                  marginBottom: '6px'
                }}
              >
                Confirm New Password
              </label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                required
                placeholder="Confirm your new password"
                style={{
                  width: '100%',
                  height: RESET_TOKENS.layout.input_height,
                  padding: '0 16px',
                  border: `1px solid ${RESET_TOKENS.colors.slate}30`,
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontFamily: RESET_TOKENS.typography.input,
                  color: RESET_TOKENS.colors.slate,
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = RESET_TOKENS.colors.literary_gold;
                  e.target.style.boxShadow = `0 0 0 3px ${RESET_TOKENS.colors.literary_gold}20`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = `${RESET_TOKENS.colors.slate}30`;
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !formData.password || !formData.confirmPassword}
              style={{
                width: '100%',
                height: '48px',
                background: (loading || !formData.password || !formData.confirmPassword) 
                  ? `${RESET_TOKENS.colors.literary_gold}50` 
                  : `linear-gradient(135deg, ${RESET_TOKENS.colors.literary_gold} 0%, ${RESET_TOKENS.colors.antique_gold} 100%)`,
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontFamily: RESET_TOKENS.typography.button,
                fontWeight: '600',
                cursor: (loading || !formData.password || !formData.confirmPassword) ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!loading && formData.password && formData.confirmPassword) {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = `0 6px 20px ${RESET_TOKENS.colors.literary_gold}30`;
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && formData.password && formData.confirmPassword) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              {loading ? (
                <>
                  <VoxSmythIcons.Loading size={16} color="white" />
                  Updating Password...
                </>
              ) : (
                <>
                  <VoxSmythIcons.CheckCircle size={16} color="white" />
                  Update Password
                </>
              )}
            </button>

            {/* Back to Sign In */}
            <div style={{ textAlign: 'center' }}>
              <p
                style={{
                  fontFamily: RESET_TOKENS.typography.input,
                  fontSize: '14px',
                  color: `${RESET_TOKENS.colors.slate}60`,
                  margin: 0
                }}
              >
                Remember your password?{' '}
                <button
                  type="button"
                  onClick={() => window.location.href = '/auth'}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: RESET_TOKENS.colors.literary_gold,
                    fontSize: '14px',
                    fontFamily: RESET_TOKENS.typography.input,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontWeight: '600'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = RESET_TOKENS.colors.antique_gold;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = RESET_TOKENS.colors.literary_gold;
                  }}
                >
                  Back to Sign In
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
